import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { availableLanguages } from '../../locales';
import useCooldown from '../vedic/shared/hooks/useCooldown';
import SingleTapGrid from '../vedic/prashnavali/shared/SingleTapGrid';
import PrashnavaliShareImagePreview from '../vedic/prashnavali/shared/PrashnavaliShareImagePreview';
import deriveChanakyaReading from '../vedic/prashnavali/chanakya/utils/deriveChanakyaReading';
import chanakyaShareImageRenderer from '../vedic/prashnavali/chanakya/utils/chanakyaShareImageRenderer';
import ChanakyaLanding from '../vedic/prashnavali/chanakya/ChanakyaLanding';
import ChanakyaResultCard from '../vedic/prashnavali/chanakya/ChanakyaResultCard';
import { CHANAKYA_GRID_PALETTE, CHANAKYA_COLORS } from '../vedic/prashnavali/chanakya/chanakyaColors';
import chanakyaData from '../vedic/prashnavali/chanakya/data/chanakya_niti.json';
import useLocale from '../vedic/hooks/useLocale';

const COOLDOWN_STORAGE_KEY = 'prashnavali_chanakya_last_query';
const COOLDOWN_DURATION_MS = 60 * 60 * 1000;

const PageContainer = styled.main`
  min-height: 100vh;
  background: ${CHANAKYA_COLORS.parchment};
  padding: 24px 20px 60px;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    padding: 16px 16px 40px;
  }
`;

const TopBar = styled.div`
  max-width: 720px;
  margin: 0 auto ${p => p.theme.spacing[4]}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: ${CHANAKYA_COLORS.primaryDark};
  font-size: 14px;
  cursor: pointer;
`;

const LangToggle = styled.div`
  display: flex;
  gap: 4px;
`;

const LangBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid ${p => p.active ? CHANAKYA_COLORS.primary : CHANAKYA_COLORS.cellBorder};
  background: ${p => p.active ? CHANAKYA_COLORS.primary : 'transparent'};
  color: ${p => p.active ? '#fff' : CHANAKYA_COLORS.primaryDark};
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  font-family: 'Noto Sans Tamil', sans-serif;
`;

const GridSection = styled.section`
  max-width: 720px;
  margin: 0 auto;
  text-align: center;
`;

const GridHeader = styled.header`
  margin-bottom: ${p => p.theme.spacing[5]}px;
`;

const HeaderTitle = styled.h2`
  font-family: 'Noto Sans Tamil', 'Playfair Display', Georgia, serif;
  font-size: 20px;
  color: ${CHANAKYA_COLORS.primaryDark};
  margin: 0 0 ${p => p.theme.spacing[2]}px;
`;

const HeaderPrompt = styled.p`
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  font-size: 14px;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0;
`;

const ChanakyaNiti = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('landing');
  const [lang, setLang] = useState('ta');
  const [selectedCellNumber, setSelectedCellNumber] = useState(null);
  const [sharePreviewOpen, setSharePreviewOpen] = useState(false);

  const t = useLocale(lang, 'chanakyaNiti');

  const cooldown = useCooldown({
    storageKey: COOLDOWN_STORAGE_KEY,
    durationMs: COOLDOWN_DURATION_MS,
  });

  const aphorism = useMemo(() => {
    if (selectedCellNumber === null) return null;
    return deriveChanakyaReading(selectedCellNumber, chanakyaData);
  }, [selectedCellNumber]);

  const handleConsult = useCallback(() => {
    if (cooldown.active) return;
    setView('grid');
  }, [cooldown.active]);

  const handleCellSelect = useCallback((cellNumber) => {
    setSelectedCellNumber(cellNumber);
    setView('result');
  }, []);

  const handleReturn = useCallback(() => {
    if (selectedCellNumber !== null) cooldown.startCooldown(selectedCellNumber);
    setSelectedCellNumber(null);
    setView('landing');
  }, [selectedCellNumber, cooldown]);

  const handleViewLastReading = useCallback(() => {
    if (cooldown.lastReadingCellIndex !== null) {
      setSelectedCellNumber(cooldown.lastReadingCellIndex);
      setView('result');
    }
  }, [cooldown.lastReadingCellIndex]);

  const gridCells = useMemo(() => chanakyaData.aphorisms.map(a => ({
    cellNumber: a.cell,
    primary: String(a.cell),
    secondary: '',
  })), []);

  const renderImage = useCallback(
    () => chanakyaShareImageRenderer(aphorism, lang),
    [aphorism, lang]
  );

  return (
    <PageContainer>
      <TopBar>
        <BackLink onClick={() => navigate('/vedic-tools')}>
          &larr; {lang === 'ta' ? 'கருவிகள்' : 'Tools'}
        </BackLink>
        <LangToggle>
          {availableLanguages.map(l => (
            <LangBtn key={l.code} active={lang === l.code} onClick={() => setLang(l.code)}>
              {l.label}
            </LangBtn>
          ))}
        </LangToggle>
      </TopBar>

      {view === 'landing' && (
        <ChanakyaLanding
          onConsult={handleConsult}
          onViewLastReading={handleViewLastReading}
          cooldown={cooldown}
          lang={lang}
        />
      )}

      {view === 'grid' && (
        <GridSection>
          <GridHeader>
            <HeaderTitle>{t.gridTitle || 'Select an Aphorism'}</HeaderTitle>
            <HeaderPrompt>{t.gridPrompt || 'Reflect on your question and tap any cell'}</HeaderPrompt>
          </GridHeader>
          <SingleTapGrid
            size={6}
            cells={gridCells}
            palette={CHANAKYA_GRID_PALETTE}
            onCellSelect={handleCellSelect}
            ariaLabel="Chanakya Niti 6 by 6 grid"
          />
        </GridSection>
      )}

      {view === 'result' && aphorism && (
        <ChanakyaResultCard
          aphorism={aphorism}
          onShare={() => setSharePreviewOpen(true)}
          onReturn={handleReturn}
          lang={lang}
        />
      )}

      {sharePreviewOpen && aphorism && (
        <PrashnavaliShareImagePreview
          open={sharePreviewOpen}
          onClose={() => setSharePreviewOpen(false)}
          renderImage={renderImage}
          shareTitle={t.shareTitle || "Chanakya's Counsel"}
          fileName="chanakya-niti.png"
          lang={lang}
        />
      )}
    </PageContainer>
  );
};

export default ChanakyaNiti;
